import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, Send } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { buildTommyUrl } from '@/constants/support';
import { MESSAGE_MAX_CHARS, MESSAGE_TOO_LONG } from '@/constants/messageLimits';

/**
 * The Contact page's "Send us a message" form (founder, 2026-10-23), which
 * replaces the published support@ address. Signed-in customers give a name
 * and a message; guests also give an email so the team can write back.
 * POST /api/contact (server/contact-form.js) decides where it lands: the
 * customer's own chat plus the inquiries desk, or a team email for a guest.
 */
const NAME_MAX = 100;

export default function ContactForm({ defaultName = '' }) {
  const [token, setToken] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [name, setName] = useState(defaultName);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState(''); // honeypot: never shown to people
  const [status, setStatus] = useState('idle'); // idle | sending | sent
  const [sentTo, setSentTo] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    supabase.auth.getSession()
      .then(({ data }) => { if (alive) setToken(data?.session?.access_token || null); })
      .catch(() => {})
      .finally(() => { if (alive) setAuthChecked(true); });
    return () => { alive = false; };
  }, []);

  useEffect(() => { if (defaultName && !name) setName(defaultName); }, [defaultName]); // eslint-disable-line react-hooks/exhaustive-deps

  const signedIn = Boolean(token);

  const submit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    setError('');
    if (!name.trim()) return setError('Please enter your name.');
    if (!signedIn && !email.trim()) return setError('Please enter an email address we can reply to.');
    if (message.trim().length < 5) return setError('Please write a message.');
    if (message.trim().length > MESSAGE_MAX_CHARS) return setError(MESSAGE_TOO_LONG);
    setStatus('sending');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ name, message, ...(signedIn ? {} : { email }), website }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || 'Your message could not be sent. Please try again, or chat with us.');
      setSentTo(json.channel || (signedIn ? 'chat' : 'email'));
      setStatus('sent');
    } catch (err) {
      setError(err.message);
      setStatus('idle');
    }
  };

  if (status === 'sent') {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" role="status">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-white text-[15px] font-medium">Thanks, {name.trim().split(' ')[0]}. Your message is on its way.</p>
            <p className="text-white/70 text-[13px] font-normal mt-1 leading-relaxed">
              {sentTo === 'chat'
                ? <>Our team will reply within 24 hours, here in your <Link to={buildTommyUrl()} className="underline hover:text-white">Defera chat</Link> and by email.</>
                : signedIn
                  ? 'Our team will reply by email within 24 hours.'
                  : 'Our team will reply to the email address you gave us within 24 hours.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const field = 'w-full rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3 text-[14px] text-white placeholder:text-white/35 font-normal focus:outline-none focus:border-white/30';
  return (
    <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 space-y-4" noValidate aria-label="Send us a message">
      <div>
        <h2 className="text-white text-[15px] font-medium">Send us a message</h2>
        <p className="text-white/55 text-[13px] font-normal mt-1">Our team replies within 24 hours.</p>
      </div>

      <label className="block">
        <span className="block text-white/70 text-[13px] font-normal mb-1.5">Name</span>
        <input className={field} value={name} maxLength={NAME_MAX} onChange={(e) => setName(e.target.value)} autoComplete="name" name="name" />
      </label>

      {authChecked && !signedIn && (
        <label className="block">
          <span className="block text-white/70 text-[13px] font-normal mb-1.5">Email</span>
          <input className={field} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" name="email" placeholder="So we can reply to you" />
        </label>
      )}

      <label className="block">
        <span className="block text-white/70 text-[13px] font-normal mb-1.5">Message</span>
        <textarea className={`${field} min-h-[140px] resize-y`} value={message} onChange={(e) => setMessage(e.target.value)} name="message" placeholder="How can we help?" />
      </label>

      {/* Honeypot: hidden from people and screen readers, filled only by bots. */}
      <input type="text" name="website" value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute left-[-9999px] w-px h-px opacity-0" />

      {error && <p className="text-[13px] text-red-300 font-normal" role="alert">{error}</p>}

      <button
        type="submit"
        disabled={status === 'sending' || !authChecked}
        className="inline-flex items-center justify-center gap-2 rounded-xl bg-white text-black px-5 py-2.5 text-[14px] font-medium disabled:opacity-60"
      >
        {status === 'sending' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        {status === 'sending' ? 'Sending…' : 'Send message'}
      </button>
    </form>
  );
}

ContactForm.propTypes = {
  /** The signed-in customer's name, to prefill the Name field. */
  defaultName: PropTypes.string,
};
